import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiZap, FiEye } from "react-icons/fi";
import "./ProductionLines.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const SubProductionLines = () => {
  useTitle("Rovion MES - Alt Üretim Hatları");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/production-lines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const [lines, setLines] = useState([]);
  const [dailyQtyByOutputId, setDailyQtyByOutputId] = useState({});
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const allowedMachineIds = useMemo(() => {
    const list = currentUser?.machineAccess || [];
    if (!Array.isArray(list) || list.length === 0) return null;
    return new Set(list.map((x) => String(x)));
  }, [currentUser]);

  const addToast = useCallback((message, type = "info") => {
    const t = { id: Date.now(), message, type };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
  }, []);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { headers } = authHeaders();
      const linesRes = await axios.get(
        `${API_URL}/api/production-lines/from-parts?subOnly=true`,
        { headers },
      );
      const start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setUTCHours(23, 59, 59, 999);
      const dailyRes = await axios.get(`${API_URL}/api/daily-production`, {
        headers,
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      const rawLines = linesRes.data?.lines || linesRes.data || [];
      const dedupedLines = (() => {
        if (!Array.isArray(rawLines)) return [];
        const map = new Map();
        rawLines.forEach((line) => {
          if (!line) return;
          const key = line._id
            ? `id:${line._id}`
            : line.code
              ? `code:${line.code}`
              : null;
          if (!key) return;
          if (!map.has(key)) map.set(key, line);
        });
        return Array.from(map.values());
      })();
      setLines(dedupedLines);
      const map = {};
      const items = dailyRes.data?.items || [];
      items.forEach((it) => {
        const oid = it.outputItemId?._id || it.outputItemId;
        if (!oid) return;
        const key = String(oid);
        map[key] = (map[key] || 0) + (Number(it.quantity) || 0);
      });
      setDailyQtyByOutputId(map);
    } catch (err) {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const handleView = useCallback(
    (line) => navigate(`/production-lines/sub/view/${line._id}`),
    [navigate],
  );

  const getMachinesSummary = (line) => {
    const items = line.machineItems || [];
    if (!items.length) return "-";
    return items
      .map((mi) => mi.machineId?.name || mi.machineId?.code || "?")
      .join(" → ");
  };

  const getTodayProduced = (line) => {
    const outId = line?.outputItemId ? String(line.outputItemId) : null;
    if (!outId) return 0;
    return dailyQtyByOutputId[outId] || 0;
  };

  const headers = [
    {
      name: "Hat Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      name: "Kod",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.code || "").localeCompare(String(b.code || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      name: "Bugün Üretim",
      dataType: "number",
      id: 2,
      customSorter: (a, b) =>
        getTodayProduced(a) - getTodayProduced(b),
    },
    { name: "İşlemler", id: 3, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("sub-production-lines-list", headers);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const visibleLines = useMemo(() => {
    if (!allowedMachineIds) return lines || [];
    return (lines || []).filter((line) =>
      (line.machineItems || []).some((mi) => {
        const mid = mi.machineId?._id || mi.machineId;
        return mid && allowedMachineIds.has(String(mid));
      }),
    );
  }, [lines, allowedMachineIds]);

  return (
    <AppPage
      title="Alt Üretim Hatları"
      subtitle="Başka ürünlere girdi olarak kullanılan alt ürünlerin üretim hatları"
      icon={<FiZap />}
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="sub-production-lines-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {visibleLines.map((line) => (
                <Tr
                  key={line._id}
                  id={line._id}
                  data={line}
                  onClick={() => handleView(line)}
                >
                  {visibleColumns.includes(0) && (
                    <Td>
                      <strong>{line.name}</strong>
                    </Td>
                  )}
                  {visibleColumns.includes(1) && <Td>{line.code}</Td>}
                  {visibleColumns.includes(2) && (
                    <Td>{getTodayProduced(line)}</Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        icon={<FiEye style={{ fontSize: "18px" }} />}
                        appearance="subtle"
                        shape="circle"
                        onClick={() => handleView(line)}
                        title="Üretim Adımları"
                      />
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>
            {(!visibleLines || visibleLines.length === 0) && (
              <div className="empty-state">
                <FiZap size={64} />
                <p>Alt üretim hattı bulunamadı</p>
                <p className="text-muted small mt-1">
                  Alt üretim hatları, `Parça–Makine` girdilerinde başka parça
                  olarak kullanılan çıktılardan otomatik çıkarılır.
                </p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(SubProductionLines, ["admin", "employee", "user"]);
